import { useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { SelectWithSearch, Button } from "@panwds/react-ui";
import { PANFieldContainer, PANHelpText, PANMutedText } from "../FormElements/styles";
import { PANWDSLabel } from "./PANWDSLabel";
import classNames from "classnames";
import { SelectItems } from '../../types';
import { find, isEqual } from 'lodash';

const useStyles = makeStyles((theme) => ({
  error: {
    border: '1px solid #D13C3C',
    borderRadius: 4,
  },
  errorText: {
    color: "#D13C3C",
  },
  buttonText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: "100%",
    textAlign: "left",
  },
  readOnly: {
    "--tw-bg-opacity": 1,
    backgroundColor: "rgb(244 245 245/var(--tw-bg-opacity))",
    opacity: 1,
  },
  button: {
    color: "#333333",
    "&:hover": {
      color: "#333333",
    }
  }
}));

export const PANWDSSelectWithSearch = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}) => {
  const classes = useStyles();
  const existerror = !!(touched && error);
  let { items = [], title, required, enableMultiSelect, enableArrayInput, dataMetrics = undefined, loading = false, disabled = false, readOnly = false, initValue, content }: { items: SelectItems[] } = props;

  // Ensure initValue is correctly processed
  let value = inputProps.value ? inputProps.value : initValue ? initValue : [];
  if (typeof value === 'string') {
    value = [value];
  } else if (Array.isArray(value)) {
    value = value.map(v => (typeof v === 'object' && v.value ? v.value : v));
  } else if (typeof value === 'object' && value.value) {
    value = [value.value];
  }

  let noneOption: any = [];
  if (enableArrayInput) {
    value = value.map((v: any) => (typeof v === 'object' && v.value ? { value: v.value } : { value: v }));
  } else {
    noneOption = props.allowSetNone && items.length ? [{ value: "None" }] : [];
    value = value.map((v: any) => ({ value: v }));
  }

  useEffect(() => {
    if (initValue && enableMultiSelect && inputProps.value.length === 0) {
      inputProps.onChange(initValue);
    }
  }, [initValue, enableMultiSelect, inputProps]);

  return (
    <PANFieldContainer>
      {title && <PANWDSLabel disabled={disabled} required={required} title={title} />}
      <SelectWithSearch
        fitToItemWidth
        itemToString={item => (item && item.text) ? `${item?.text} ${item?.value}` : `${item?.value}`}
        items={[...noneOption, ...items]}
        selectedItem={value}
        onChange={({ selectedItem, selectedItems }) => {
          if (enableArrayInput) {
            inputProps.onChange(selectedItems.map((item: any) => item.value));
          } else {
            inputProps.onChange(selectedItem.value);
          }
          if (props.onChange) {
            props.onChange(selectedItem.value);
          }
        }}
        enableMultiSelect={enableMultiSelect}
        button={
          <Button addClassName={classNames(classes.button, readOnly && classes.readOnly)} isMenu fullWidth dataMetrics={dataMetrics ? dataMetrics + '-button' : title + '-button'}>
            <span className={classes.buttonText}>
              {value.length
                ? value.map((item: any) => {
                  let selectedItem = find(items, function (o) { return isEqual(o.value, item.value) });
                  return selectedItem && selectedItem.text ? String(selectedItem.text) : String(selectedItem?.value || item.value);
                }).join(", ")
                : "Type to select an item"}
            </span>
          </Button>
        }
        formatOption={{
          height: (item, defaultSize) => defaultSize * 1.2,
          view: (item) => (
            <div className="tw-flex tw-gap-2 tw-items-center">
              {item.text ? item.text : item.value}
            </div>
          ),
        }}
        fullWidth
        dataMetrics={dataMetrics}
        isLoading={loading}
        disabled={disabled || readOnly}
      />
      {existerror && (<PANHelpText>{error}</PANHelpText>)}
      {props?.muted && (<PANMutedText>{props.muted}</PANMutedText>)}
    </PANFieldContainer>
  );
};
